import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useLiveRefresh } from '../../context/LiveUpdatesContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Timer, Download } from 'lucide-react';
import { PageHeader, DataTable, Person, Button, Select } from '../../components';
import { agencyService } from '../../services/agencyService';
import { BookingDetail } from './AgencyBookings';
import {
  CARD, inputClass, FieldLabel, FilterTabs, HoursPill, errorMessage, dayLabel, timeRange, clockOf, hoursOf,
  money, localToday, addDays, Segmented,
} from './shared';

/**
 * Agency > Hours — what the agency's staff worked, from their own clocking in
 * the app. The care home approves (or corrects) the hours; the agency reads
 * them and exports the approved ones for invoicing. Nothing here creates an
 * invoice — the CSV is the hand-off.
 */

export const Stat = ({ label, value, tone }) => (
  <div className={`${CARD} px-5 py-4`}>
    <p className="text-[12px] font-semibold text-[#64748B] dark:text-gray-400">{label}</p>
    <p className={`mt-1 text-[22px] font-bold tabular-nums ${tone === 'warn' ? 'text-amber-600' : tone === 'crit' ? 'text-rose-600' : 'text-[#0B1220] dark:text-gray-100'}`}>{value}</p>
  </div>
);

const AgencyHours = () => {
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'staff' ? 'staff' : 'shifts';
  const setView = (v) => setParams((p) => { const next = new URLSearchParams(p); next.set('view', v); return next; }, { replace: true });
  const [from, setFrom] = useState(addDays(localToday(), -30));
  const [to, setTo] = useState(localToday());
  const [status, setStatus] = useState('all');
  const [homeId, setHomeId] = useState('');
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const res = await agencyService.hours({ from, to, home_id: homeId || undefined });
      setRows(res.data || []);
      setTotals(res.meta?.totals || {});
    } catch (err) {
      if (!quiet) toast.error(errorMessage(err, 'Could not load hours.'));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [from, to, homeId]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(['agency'], () => load({ quiet: true }));

  const homes = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => r.home && map.set(r.home.id, r.home.name));
    return [...map].map(([value, label]) => ({ value: String(value), label }));
  }, [rows]);

  const list = rows.filter((r) => status === 'all'
    || (status === 'missed' ? ['missed', 'no_show'].includes(r.state) : r.hours === status));

  const exportCsv = async () => {
    setExporting(true);
    try {
      await agencyService.exportHours({ from, to, home_id: homeId || undefined });
    } catch (err) {
      toast.error(errorMessage(err, 'Could not export.'));
    } finally {
      setExporting(false);
    }
  };

  const exportStaffCsv = async () => {
    setExporting(true);
    try {
      await agencyService.exportTimesheets({ from, to, home_id: homeId || undefined });
    } catch (err) {
      toast.error(errorMessage(err, 'Could not export.'));
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    { key: 'shift_date', header: 'Shift', sortable: true, render: (b) => <span className="text-[13px] font-semibold">{dayLabel(b.shift_date)} · {timeRange(b)}</span> },
    { key: 'worker', header: 'Staff', render: (b) => <Person name={b.worker?.name} sub={b.worker?.user_id} image={b.worker?.avatar_url} /> },
    { key: 'home', header: 'Care home', render: (b) => b.home?.name },
    { key: 'actual', header: 'Clocked', render: (b) => (b.actual?.clock_in ? `${clockOf(b.actual.clock_in)}–${b.actual.clock_out ? clockOf(b.actual.clock_out) : '…'}` : '—') },
    { key: 'worked', header: 'Hours', render: (b) => (b.actual ? hoursOf(b.actual.worked_minutes) : '—') },
    { key: 'charge', header: 'Charge', render: (b) => money(b.charge) },
    { key: 'hours', header: 'Status', render: (b) => <HoursPill booking={b} /> },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Hours"
        subtitle="Worked hours from your staff's app clocking. Care homes approve them; export the approved hours for invoicing."
        actions={(
          <>
            <Segmented value={view} onChange={setView} options={[{ value: 'shifts', label: 'Shifts' }, { value: 'staff', label: 'By staff' }]} />
            {view === 'shifts'
              ? <Button leftIcon={<Download className="w-4 h-4" />} loading={exporting} onClick={exportCsv}>Export approved (CSV)</Button>
              : <Button leftIcon={<Download className="w-4 h-4" />} loading={exporting} onClick={exportStaffCsv}>Export by staff (CSV)</Button>}
          </>
        )}
      />
      {view === 'staff' ? (
        <>
          <div className={`${CARD} p-4 flex flex-wrap items-end gap-3`}>
            <div>
              <FieldLabel>From</FieldLabel>
              <input type="date" className={`${inputClass} w-[160px]`} value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div>
              <FieldLabel>To</FieldLabel>
              <input type="date" className={`${inputClass} w-[160px]`} value={to} min={from} onChange={(e) => setTo(e.target.value)} />
            </div>
            {homes.length > 0 && (
              <div>
                <FieldLabel>Care home</FieldLabel>
                <Select value={homeId} onChange={(v) => setHomeId(v)} options={[{ value: '', label: 'All care homes' }, ...homes]} className="w-[220px]" />
              </div>
            )}
          </div>
          <StaffTotals from={from} to={to} homeId={homeId} />
        </>
      ) : (
      <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Approved hours" value={hoursOf(totals.approved_minutes)} />
        <Stat label="Approved charge" value={money(totals.approved_charge || 0)} />
        <Stat label="Waiting for the home" value={totals.to_approve || 0} tone={totals.to_approve ? 'warn' : undefined} />
        <Stat label="Missed clocking" value={totals.missed || 0} tone={totals.missed ? 'crit' : undefined} />
      </div>
      <div className={`${CARD} p-4 flex flex-wrap items-end gap-3`}>
        <div>
          <FieldLabel>From</FieldLabel>
          <input type="date" className={`${inputClass} w-[160px]`} value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <FieldLabel>To</FieldLabel>
          <input type="date" className={`${inputClass} w-[160px]`} value={to} min={from} onChange={(e) => setTo(e.target.value)} />
        </div>
        {homes.length > 0 && (
          <div>
            <FieldLabel>Care home</FieldLabel>
            <Select value={homeId} onChange={(v) => setHomeId(v)} options={[{ value: '', label: 'All care homes' }, ...homes]} className="w-[220px]" />
          </div>
        )}
      </div>
      <FilterTabs
        value={status}
        onChange={setStatus}
        tabs={[
          { value: 'all', label: 'All', count: rows.length },
          { value: 'to_approve', label: 'Waiting for the home', count: rows.filter((r) => r.hours === 'to_approve').length },
          { value: 'approved', label: 'Approved', count: rows.filter((r) => r.hours === 'approved').length },
          { value: 'missed', label: 'Missed / did not attend', count: rows.filter((r) => ['missed', 'no_show'].includes(r.state)).length },
        ]}
      />
      <DataTable
        columns={columns}
        data={list}
        loading={loading}
        rowKey={(b) => b.id}
        onRowClick={setOpen}
        emptyIcon={Timer}
        emptyTitle="No hours in this period"
        emptyText="Hours appear once your staff clock in at a care home."
      />
      </>
      )}
      <BookingDetail booking={open} onClose={() => setOpen(null)} />
    </div>
  );
};

/** Hours by staff — one row per person for the period. Opens their Work tab. */
const StaffTotals = ({ from, to, homeId }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // `tick` re-runs it quietly when the live refresh says hours changed.
  const [tick, setTick] = useState(0);
  const quietRef = useRef(false);
  useLiveRefresh(['agency'], () => { quietRef.current = true; setTick((t) => t + 1); });
  useEffect(() => {
    let live = true;
    const quiet = quietRef.current;
    quietRef.current = false;
    if (!quiet) setLoading(true);
    agencyService.timesheets({ from, to, home_id: homeId || undefined })
      .then((res) => { if (live) { setRows(res.data || []); setTotals(res.meta?.totals || {}); } })
      .catch((err) => { if (!quiet) toast.error(errorMessage(err, 'Could not load hours by staff.')); })
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [from, to, homeId, tick]);

  const q = search.trim().toLowerCase();
  const list = q ? rows.filter((r) => [r.worker.name, r.worker.user_id].some((v) => String(v || '').toLowerCase().includes(q))) : rows;

  const columns = [
    { key: 'name', header: 'Staff', sortable: true, sortAccessor: (r) => r.worker.name, render: (r) => <Person name={r.worker.name} sub={`${r.worker.user_id}${r.worker.job_role ? ` · ${r.worker.job_role}` : ''}`} image={r.worker.avatar_url} /> },
    { key: 'shifts_worked', header: 'Shifts', sortable: true, render: (r) => <span className="text-[13px]"><strong>{r.shifts_worked}</strong> of {r.shifts_booked}</span> },
    { key: 'worked_minutes', header: 'Worked', sortable: true, render: (r) => hoursOf(r.worked_minutes) },
    { key: 'approved_minutes', header: 'Approved', sortable: true, render: (r) => hoursOf(r.approved_minutes) },
    { key: 'to_approve', header: 'Waiting', sortable: true, render: (r) => (r.to_approve ? <span className="text-amber-600 font-semibold">{r.to_approve}</span> : '0') },
    {
      key: 'on_time_rate', header: 'On time', sortable: true,
      render: (r) => (
        <span className="text-[12.5px]">
          {r.on_time_rate == null ? '—' : `${r.on_time_rate}%`}
          {(r.late || r.missed || r.no_show) ? <span className="block text-[11.5px] text-rose-600">{[r.late && `${r.late} late`, r.missed && `${r.missed} missed`, r.no_show && `${r.no_show} no-show`].filter(Boolean).join(' · ')}</span> : null}
        </span>
      ),
    },
    { key: 'approved_charge', header: 'Charge', sortable: true, render: (r) => money(r.approved_charge) },
    { key: 'homes', header: 'Care homes', render: (r) => <span className="text-[12.5px] text-[#64748B]">{r.homes.join(', ') || '—'}</span> },
  ];

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Hours worked" value={hoursOf(totals.worked_minutes)} />
        <Stat label="Hours approved" value={hoursOf(totals.approved_minutes)} />
        <Stat label="Staff who worked" value={totals.staff_working || 0} />
        <Stat label="On time" value={totals.on_time_rate == null ? '—' : `${totals.on_time_rate}%`} tone={totals.on_time_rate != null && totals.on_time_rate < 90 ? 'warn' : undefined} />
      </div>
      <DataTable
        columns={columns}
        data={list}
        loading={loading}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search staff…"
        defaultSort={{ key: 'worked_minutes', dir: 'desc' }}
        rowKey={(r) => r.worker.id}
        onRowClick={(r) => navigate(`/agency/staff?open=${r.worker.id}&tab=work`)}
        emptyIcon={Timer}
        emptyTitle="No staff in this period"
      />
    </>
  );
};

export default AgencyHours;
